import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type DashboardSummaryResponse =
  components["schemas"]["DashboardSummaryResponse"];
export type OpenBySeverityItem = components["schemas"]["OpenBySeverityItem"];
export type CategoryCountItem = components["schemas"]["CategoryCountItem"];
export type TrendBucket = components["schemas"]["TrendBucket"];
export type TrendGroupBy = components["schemas"]["TrendGroupBy"];
export type TrendPoint = components["schemas"]["TrendPoint"];
export type DashboardTrendResponse =
  components["schemas"]["DashboardTrendResponse"];
export type TagCombinationItem = components["schemas"]["TagCombinationItem"];
export type TagCombinationsResponse =
  components["schemas"]["TagCombinationsResponse"];

export interface TrendParams {
  bucket: TrendBucket;
  groupBy: TrendGroupBy;
  months: number;
}

export interface TagCombinationsParams {
  limit?: number;
  window?: number;
}

export const dashboardKeys = {
  all: ["dashboard"] as const,
  summary: () => ["dashboard", "summary"] as const,
  trend: (params: TrendParams) => ["dashboard", "trend", params] as const,
  tagCombinations: (params: TagCombinationsParams) =>
    ["dashboard", "tag-combinations", params] as const,
};

/** GET /dashboard/summary - the KPI tile numbers (F-020, any role). */
export function useDashboardSummary() {
  return useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: () => apiFetch<DashboardSummaryResponse>("/dashboard/summary"),
  });
}

/**
 * GET /dashboard/trend (F-023). Manager-only on the server; callers gate via
 * `enabled` (`can("dashboard.trends")`) so nobody else fires the request and
 * never surface a 403.
 */
export function useTrend(params: TrendParams, opts: { enabled: boolean }) {
  const sp = new URLSearchParams({
    bucket: params.bucket,
    groupBy: params.groupBy,
    months: String(params.months),
  });
  return useQuery({
    queryKey: dashboardKeys.trend(params),
    queryFn: () =>
      apiFetch<DashboardTrendResponse>(`/dashboard/trend?${sp.toString()}`),
    enabled: opts.enabled,
  });
}

/**
 * GET /dashboard/tag-combinations (US-020). Manager-only on the server; same
 * `enabled` gating as the trend query. The < 10 tagged-incidents threshold is
 * evaluated in the FE against `meta.taggedIncidents`.
 */
export function useTagCombinations(
  params: TagCombinationsParams,
  opts: { enabled: boolean },
) {
  const sp = new URLSearchParams();
  if (params.limit !== undefined) sp.set("limit", String(params.limit));
  if (params.window !== undefined) sp.set("window", String(params.window));
  const qs = sp.toString();
  return useQuery({
    queryKey: dashboardKeys.tagCombinations(params),
    queryFn: () =>
      apiFetch<TagCombinationsResponse>(
        `/dashboard/tag-combinations${qs ? `?${qs}` : ""}`,
      ),
    enabled: opts.enabled,
  });
}
